// Story 1.28 — TanStack Query mutations for admin portfolio-application
// approve / reject / bulk-approve. Class-based error shape mirrors
// Story 1.25's WithdrawPortfolioApplicationError pattern so UI code
// can branch on error.code.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export class AdminPortfolioApplicationError extends Error {
  readonly code: string;
  readonly status: number;
  readonly currentStatus?: string;
  readonly portfolioApplicationId?: string;
  readonly field?: string;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    currentStatus?: string;
    portfolioApplicationId?: string;
    field?: string;
  }) {
    super(args.message);
    this.name = "AdminPortfolioApplicationError";
    this.code = args.code;
    this.status = args.status;
    this.currentStatus = args.currentStatus;
    this.portfolioApplicationId = args.portfolioApplicationId;
    this.field = args.field;
  }
}

async function parseError(response: Response): Promise<AdminPortfolioApplicationError> {
  let payload: Record<string, unknown> = {};
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    // body not JSON
  }
  const err = (payload?.error ?? {}) as Record<string, unknown>;
  return new AdminPortfolioApplicationError({
    code: (err.code as string) ?? "UNKNOWN",
    message:
      (err.message as string) ?? `Request failed (HTTP ${response.status})`,
    status: response.status,
    currentStatus: err.current_status as string | undefined,
    portfolioApplicationId: err.portfolio_application_id as
      | string
      | undefined,
    field: err.field as string | undefined,
  });
}

// ------------------------------------------------------------------
// Approve
// ------------------------------------------------------------------

export type ApprovePortfolioApplicationResponse = {
  data: {
    portfolio_application_id: string;
    user_id: string;
    portfolio_id: string;
    portfolio_title: string;
    creator_display_name: string | null;
    creator_handle: string | null;
    status: "approved";
    approved_at: string;
  };
};

async function approveOnce(
  id: string,
): Promise<ApprovePortfolioApplicationResponse> {
  const response = await fetch(
    `/api/v1/admin/portfolio-applications/${id}/approve`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as ApprovePortfolioApplicationResponse;
}

export function useApprovePortfolioApplicationMutation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => approveOnce(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio-application", id],
      });
    },
  });
}

// ------------------------------------------------------------------
// Reject
// ------------------------------------------------------------------

export type RejectPortfolioApplicationResponse = {
  data: {
    portfolio_application_id: string;
    user_id: string;
    portfolio_id: string;
    portfolio_title: string;
    creator_display_name: string | null;
    status: "rejected";
    rejection_reason: string | null;
    rejected_at: string;
  };
};

async function rejectOnce(
  id: string,
  reason: string | null,
): Promise<RejectPortfolioApplicationResponse> {
  const response = await fetch(
    `/api/v1/admin/portfolio-applications/${id}/reject`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as RejectPortfolioApplicationResponse;
}

export function useRejectPortfolioApplicationMutation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | null) => rejectOnce(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio-applications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio-application", id],
      });
    },
  });
}

// ------------------------------------------------------------------
// Bulk approve
// ------------------------------------------------------------------

export type BulkApproveSuccess = {
  portfolio_application_id: string;
  user_id: string;
  portfolio_id: string;
  portfolio_title: string;
  creator_display_name: string | null;
  creator_handle: string | null;
  status?: "approved";
  approved_at: string;
};

export type BulkApproveFailure = {
  portfolio_application_id: string;
  code: string;
  message: string;
};

export type BulkApproveResponse = {
  data: {
    successes: BulkApproveSuccess[];
    failures: BulkApproveFailure[];
  };
};

// Fresh key per submit attempt — the bulk-approve URL sits under the
// backend's /api/v1/admin/portfolios idempotency prefix, which 400s
// (IDEMPOTENCY_KEY_REQUIRED) any POST without an x-idempotency-key.
function freshIdempotencyKey(): string {
  return crypto.randomUUID();
}

async function bulkApproveOnce(
  portfolioId: string,
  ids: string[],
): Promise<BulkApproveResponse> {
  const response = await fetch(
    `/api/v1/admin/portfolios/${portfolioId}/portfolio-applications/bulk-approve`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: JSON.stringify({ portfolio_application_ids: ids }),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as BulkApproveResponse;
}

export function useBulkApprovePortfolioApplicationsMutation(portfolioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkApproveOnce(portfolioId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio-applications"],
      });
    },
  });
}
